import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';

import Menu from './Menu/Menu';
import Field from './Field/Field';
import { Button } from './Button/Button';
import { Question } from './Question/Question';
import { Event } from './Event/Event';
import { SubmitButton } from './SubmitButton/SubmitButton';
import { PreviousPage } from './PreviousPage/PreviousPage';
import { ForwardPage } from './ForwardPage/ForwardPage';
import PaymentOptions from './PaymentOptions/PaymentOptions';
import { Discount } from './Discount/Discount';
import { OrderSummary } from './OrderSummary/OrderSummary';
import { GroupRegistration } from './GroupRegistration/GroupRegistration';
import { FileUploader } from './FileUploader/FileUploader';
import { ProfileImageUploader } from './ProfileImageUploader/ProfileImageUploader';
import { Image } from './Image/Image';
import { TextContent } from './TextContent/TextContent';
import { IframeModule } from './Iframe/Iframe';
import { ContentView } from './ContentView/ContentView';
import { Paginator } from './Paginator/Paginator';
import { Space } from './Space/Space';
import { VideoModule } from './Video/VideoModule';
import { EventGroup } from './EventGroup/EventGroup';
import GoogleMaps from './GoogleMaps/GoogleMaps';
import CalendarInvitation from './CalendarInvitation/CalendarInvitation';
import QRCode from './QRCode/QRCode';
import { ImageGroup } from './ImageGroup/ImageGroup';
import { Countdown } from './Countdown/Countdown';
import { VoucherModule } from './VoucherModule/VoucherModule';
import { ExhibitorInviteList } from './ExhibitorInviteList/ExhibitorInviteList';
import Printer from './Printer/Printer';
import ContentGroup from './ContentGroup/ContentGroup';
import { cssIdGenerator } from '../utils/identifier.utils';
import Hotel from './Hotel/Hotel';
import { ErrorBoundary } from '@sentry/react';
import ModuleFallback from '../components/ModuleFallback/ModuleFallback';
import ConfirmMeeting from './ConfirmMeeting/ConfirmMeeting';
import MatchMakingModule from './MatchMaking/MatchMaking';
import _ from 'lodash';
import Tickets from './Tickets/Tickets';
import { createPortal } from 'react-dom';
import CustomField from './CustomField/CustomField';
import AttendeeList from './AttendeeList/AttendeeList';
import ExhibitorList from './ExhibitorList/ExhibitorList';
import { AddToHomeScreen } from './add-to-home-screen/add-to-home-screen';
import Unregister from './Unregister/Unregister';
import Accreditation from './Accreditation/Accreditation';
import { FileCatalog } from './file-catalog/file-catalog';

const NON_AUTO_HEIGHT_MODULES = ['SPACE', 'GOOGLE_MAPS', 'CONTENT_VIEW', 'IFRAME'];

export const ContentLayoutModule = observer(props => {
  const [aspectRatio, setAspectRatio] = useState(null);
  const [renderSticky, setRenderSticky] = useState(false);
  const moduleRef = useRef();

  const { contentLayoutModuleId, type, settings, height, modulesRefs, conditions, hiddenElements, isSmartphone, activePersonNo } = props;

  const cssId = props.cssId || cssIdGenerator(contentLayoutModuleId);
  const settingsOverride = { ...settings };

  useEffect(() => {
    props.checkConditions(contentLayoutModuleId);
  }, [activePersonNo]);

  useEffect(() => {
    if (settings?.aspectRatio && !aspectRatio) {
      setAspectRatio(settings.aspectRatio);
    } else if (!settings?.aspectRatio && aspectRatio) {
      setAspectRatio(null);
    }
  }, [settings?.aspectRatio]);

  if (!modulesRefs) return null;
  if ((conditions[contentLayoutModuleId] && conditions[contentLayoutModuleId].hidden == true) || (isSmartphone && settingsOverride.hideOnMobile))
    settingsOverride.hidden = true;
  if (conditions[contentLayoutModuleId] && conditions[contentLayoutModuleId].required == true) settingsOverride.required = true;
  if (conditions[contentLayoutModuleId] && conditions[contentLayoutModuleId].readonly == true) settingsOverride.readonly = true;
  if (renderSticky) {
    return createPortal(
      <div className="fixed left-0 top-0 w-screen flex justify-center items-center content-wrapper" style={{ maxWidth: '100%', padding: 0 }}>
        <div
          id={cssId}
          ref={moduleRef}
          className={`content-wrapper ${settings.prefixClassName}`}
          style={{
            display: settingsOverride.hidden ? 'none' : null,
            maxHeight: aspectRatio ? 'unset' : NON_AUTO_HEIGHT_MODULES.includes(type) ? `calc(${height}px )` : undefined,
            aspectRatio: aspectRatio ? `${aspectRatio} / 1` : undefined,
          }}
        >
          <Module {...props} settings={settingsOverride} setAspectRatio={setAspectRatio} setRenderSticky={setRenderSticky} renderSticky={renderSticky} />
        </div>
      </div>,
      document.getElementById('body')
    );
  }

  return (
    <div
      id={`${cssId}-grid-item`}
      className={'wrapper-grid-item'}
      style={{ width: '100%', display: 'flex', height: '100%', justifyContent: 'center', padding: 0, alignItems: 'flex-start', minHeight: 0 }}
    >
      <div
        id={cssId}
        ref={moduleRef}
        className={`content-wrapper ${settings.imitateField ? 'cms-field' : settings.prefixClassName}`}
        style={{
          display: settingsOverride.hidden ? 'none' : null,
          marginTop: hiddenElements ? hiddenElements * -1 + 'px' : null,
          height: aspectRatio ? 'unset' : NON_AUTO_HEIGHT_MODULES.includes(type) ? `calc(${height}px )` : undefined,
          aspectRatio: aspectRatio ? `${aspectRatio}` : undefined,
          width: '100%',
          minHeight: 0,
        }}
      >
        <ErrorBoundary fallback={err => <ModuleFallback {...{ ...err, ...props }} />}>
          <Module
            {...props}
            settings={settingsOverride}
            setAspectRatio={setAspectRatio}
            setRenderSticky={setRenderSticky}
            renderSticky={renderSticky}
            checkHeights={() => null}
            moduleRef={moduleRef}
          />
        </ErrorBoundary>
      </div>
    </div>
  );
});

const Module = ({ type, ...rest }) => {
  switch (type) {
    case 'UNREGISTER': {
      return <Unregister {...rest} />;
    }
    case 'ACCREDITATION': {
      return <Accreditation {...rest} />;
    }
    case 'FIELD':
      return <Field {...rest} />;
    case 'IMAGE':
      return <Image {...rest} />;
    case 'COUNTDOWN':
      return <Countdown {...rest} />;
    case 'IMAGE_GROUP':
      return <ImageGroup {...rest} />;
    case 'QUESTION':
      return <Question {...rest} />;
    case 'SPACE':
      return <Space {...rest} />;
    case 'IFRAME':
      return <IframeModule {...rest} />;
    case 'GOOGLE_MAPS':
      return <GoogleMaps {...rest} />;
    case 'CALENDAR_INVITATION':
      return <CalendarInvitation {...rest} />;
    case 'QR_CODE':
      return <QRCode {...rest} />;
    case 'CONFIRM_MEETING':
      return <ConfirmMeeting {...rest} />;
    case 'EVENT':
      return <Event {...rest} />;
    case 'EVENT_GROUP':
      return <EventGroup {...rest} />;
    case 'MENU':
      return <Menu {...rest} />;
    case 'SUBMIT_BUTTON':
      return <SubmitButton {...rest} />;
    case 'ADD_TO_HOME_SCREEN':
      return <AddToHomeScreen {...rest} />;
    case 'CUSTOMFIELD': {
      return <CustomField {...rest} />;
    }
    case 'FORWARD_PAGE':
      return <ForwardPage {...rest} />;
    case 'PREVIOUS_PAGE':
      return <PreviousPage {...rest} />;
    case 'BUTTON':
      return <Button {...rest} />;
    case 'PRINTER': {
      return <Printer {...rest} />;
    }
    case 'CONTENT_GROUP': {
      return <ContentGroup {...rest} />;
    }
    case 'PAYMENT_OPTIONS':
    case 'PAYMENT_OPTIONS_TWO':
      return <PaymentOptions {...rest} />;
    case 'DISCOUNT':
      return <Discount {...rest} />;
    case 'ORDER_SUMMARY':
      return <OrderSummary {...rest} />;
    case 'GROUP_REGISTRATION':
      return <GroupRegistration {...rest} />;
    case 'PROFILEIMAGE_UPLOADER':
      return <ProfileImageUploader {...rest} />;
    case 'FILE_UPLOADER':
      return <FileUploader {...rest} />;
    case 'TEXT_CONTENT':
      return <TextContent {...rest} />;
    case 'CONTENT_VIEW':
      return <ContentView {...rest} />;
    case 'PAGINATOR':
      return <Paginator {...rest} />;
    case 'VIDEO':
      return <VideoModule {...{ ...rest, type }} />;
    case 'HOTEL_MODULE': {
      return <Hotel {...rest} />;
    }
    case 'MATCHMAKING':
      return <MatchMakingModule {...rest} />;
    case 'VOUCHER':
      return <VoucherModule {...rest} />;
    case 'EXHIBITOR_INVITE_LIST':
      return <ExhibitorInviteList {...rest} />;
    case 'TICKETS':
      return <Tickets {...rest} />;
    case 'ATTENDEE_LIST':
      return <AttendeeList {...rest} />;
    case 'EXHIBITOR_LIST':
      return <ExhibitorList {...rest} />;
    case 'FILE_CATALOG':
      return <FileCatalog {...rest} />;
    case 'LINK':
      return (
        <div className="jir-scroll" style={{ maxHeight: 200, overflow: 'auto' }}>
          <a>Länk</a>
        </div>
      );
    default:
      return <span>Module Type {type} not found.</span>;
  }
};
